import React from 'react'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'

interface CallControlDockProps {
  children: React.ReactNode
  /** Render an additional pinned destructive action (e.g. end-call) below the dock. */
  footer?: React.ReactNode
  className?: string
  /** When true, the dock is positioned absolutely at the bottom of its parent. */
  floating?: boolean
}

/**
 * CallControlDock — floating glass strip that holds in-call control buttons.
 * Composes SafeAreaBottom so the iOS home-indicator never overlaps the controls
 * (CLAUDE.md §8). Visually mirrors `.ds-tabbar` styling: 40px radius, blur 40 +
 * saturate 180, midnight-lift shadow.
 */
export function CallControlDock({
  children,
  footer,
  className,
  floating = false,
}: CallControlDockProps) {
  return (
    <SafeAreaBottom
      extra={floating ? 16 : 0}
      className={[
        floating ? 'absolute left-0 right-0 bottom-0' : '',
        'pointer-events-none flex flex-col items-stretch gap-3 px-4',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        role="toolbar"
        aria-label="Call controls"
        className="pointer-events-auto"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: 8,
          padding: '12px 14px',
          background: 'rgba(10,10,12,0.82)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '0.5px solid var(--midnight-border)',
          borderRadius: 'var(--r-2xl, 40px)',
          boxShadow:
            '0 20px 50px -12px rgba(0,0,0,0.7), 0 4px 12px rgba(0,0,0,0.5)',
        }}
      >
        {children}
      </div>
      {footer ? <div className="pointer-events-auto">{footer}</div> : null}
    </SafeAreaBottom>
  )
}

// ─── Single dock button ──────────────────────────────────────────────────────

type DockBtnTone = 'neutral' | 'active' | 'danger'

interface CallControlDockButtonProps {
  onClick: () => void
  icon: React.ReactNode
  label: string
  tone?: DockBtnTone
  pressed?: boolean
  disabled?: boolean
  className?: string
}

const TONE_STYLES: Record<DockBtnTone, { bg: string; border: string; color: string }> = {
  neutral: {
    bg: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.10)',
    color: 'rgba(255,255,255,0.85)',
  },
  active: {
    bg: 'rgba(255,255,255,0.96)',
    border: 'rgba(255,255,255,1)',
    color: '#000000',
  },
  danger: {
    bg: 'rgba(168,75,62,0.18)',
    border: 'rgba(168,75,62,0.45)',
    color: '#E1A29A',
  },
}

/**
 * Slot for a single icon button inside `<CallControlDock>`. 56×56 circular,
 * MIDNIGHT glass tones, scales 0.94 on :active. Honors `prefers-reduced-motion`.
 */
export function CallControlDockButton({
  onClick,
  icon,
  label,
  tone = 'neutral',
  pressed,
  disabled,
  className,
}: CallControlDockButtonProps) {
  const t = TONE_STYLES[tone]
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      className={['call-dock-btn', className].filter(Boolean).join(' ')}
      style={{
        width: 56,
        height: 56,
        borderRadius: '9999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: t.bg,
        border: `0.5px solid ${t.border}`,
        color: t.color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        touchAction: 'manipulation',
        transition: 'all 0.2s cubic-bezier(0.2,0.8,0.2,1)',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {icon}
    </button>
  )
}
